import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, FlaskConical, Lock, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FormatBadge, Spinner } from "@/components/common";
import { api, media, naira, errMsg, downloadTemplate } from "@/lib/api";

export function Checkout() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [provider, setProvider] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/orders/quote/${id}`).then((r) => { setQ(r.data); setProvider(r.data.providers[0]?.name || ""); }).catch((e) => { toast.error(errMsg(e)); nav(`/template/${id}`); });
    if (sp.get("cancelled")) toast.info("Payment cancelled. You can try again.");
  }, [id]); // eslint-disable-line

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", { template_id: id, provider, origin_url: window.location.origin });
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (!q) return <Spinner />;
  const t = q.template;
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <p className="eyebrow mb-2">Secure checkout</p>
      <h1 className="text-3xl font-bold sm:text-4xl">Complete your purchase</h1>
      <div className="mt-8 grid gap-6 md:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <div className="card-surface flex gap-4 p-4" data-testid="checkout-template">
            <img src={media(t.cover_image)} alt="" className="h-28 w-24 rounded-xl object-cover" />
            <div className="min-w-0">
              <h2 className="font-sans text-lg font-semibold">{t.title}</h2>
              <p className="text-sm text-muted-foreground">By {t.seller_name}</p>
              <div className="mt-2 flex gap-1">{t.file_formats.map((f) => <FormatBadge key={f} f={f} />)}</div>
            </div>
          </div>
          <div className="card-surface p-5">
            <p className="mb-4 font-semibold">Payment method</p>
            {q.providers.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="no-providers-msg">No payment provider is configured yet. The site owner must add PAYSTACK_SECRET_KEY, FLUTTERWAVE_SECRET_KEY or STRIPE_SECRET_KEY to the backend environment.</p>
            ) : (
              <div className="grid gap-3">
                {q.providers.map((p) => (
                  <button key={p.name} data-testid={`provider-${p.name}`} onClick={() => setProvider(p.name)}
                    className={`flex items-center justify-between rounded-xl border p-4 text-left transition-colors ${provider === p.name ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                    <span className="flex items-center gap-3">{p.test ? <FlaskConical size={18} className="text-amber-500" /> : <ShieldCheck size={18} className="text-primary" />}<span className="text-sm font-medium">{p.label}</span></span>
                    <span className={`h-4 w-4 rounded-full border-2 ${provider === p.name ? "border-primary bg-primary" : "border-border"}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="card-surface h-fit p-5">
          <p className="mb-4 font-semibold">Order summary</p>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Template price</span><span data-testid="checkout-price">{naira(q.price)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Service fee ({q.service_fee_percent}%)</span><span data-testid="checkout-fee">{naira(q.service_fee)}</span></div>
            <div className="flex justify-between border-t border-border pt-3 text-base font-bold"><span>Total</span><span data-testid="checkout-total">{naira(q.total)}</span></div>
          </div>
          {q.purchased ? (
            <Button asChild className="mt-6 w-full" data-testid="already-owned-btn"><Link to="/purchases">You own this — go to downloads</Link></Button>
          ) : q.is_owner ? (
            <p className="mt-6 text-sm text-muted-foreground">This is your own template.</p>
          ) : (
            <Button size="lg" className="mt-6 w-full" disabled={!provider || busy} onClick={pay} data-testid="pay-now-btn">
              {busy ? <Loader2 className="animate-spin" /> : <><Lock size={15} className="mr-2" />Pay {naira(q.total)}</>}
            </Button>
          )}
          <p className="mt-4 text-xs text-muted-foreground">Payment is verified on our server before your download unlocks.</p>
        </div>
      </div>
    </div>
  );
}

export function PaymentCallback() {
  const [sp] = useSearchParams();
  const reference = sp.get("reference") || sp.get("trxref") || sp.get("tx_ref");
  const [res, setRes] = useState(null);
  useEffect(() => {
    let n = 0; let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/verify/${reference}`);
        setRes(data);
        if (data.status !== "success" && data.status !== "failed" && n++ < 10 && !stop) setTimeout(poll, 2500);
      } catch (e) { setRes({ status: "failed", failure_reason: errMsg(e) }); }
    };
    poll();
    return () => { stop = true; };
  }, [reference]);

  const ok = res?.status === "success";
  const failed = res?.status === "failed";
  return (
    <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="payment-result">
      {!res || (!ok && !failed) ? (
        <><Loader2 className="mx-auto animate-spin text-primary" size={44} /><h1 className="mt-6 text-2xl font-bold">Verifying payment…</h1><p className="mt-2 text-sm text-muted-foreground">We confirm every payment with the provider before unlocking downloads.</p></>
      ) : ok ? (
        <>
          <CheckCircle2 className="mx-auto text-emerald-500" size={56} />
          <h1 className="mt-6 text-3xl font-bold" data-testid="payment-success-title">Payment successful</h1>
          <p className="mt-2 text-muted-foreground">{res.title} is now in your purchases.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button data-testid="callback-download-btn" onClick={() => downloadTemplate(res.template_id).catch((e) => toast.error(errMsg(e)))}><Download size={16} className="mr-2" />Download now</Button>
            <Button variant="outline" asChild data-testid="go-purchases-btn"><Link to="/purchases">My Purchases</Link></Button>
          </div>
        </>
      ) : (
        <>
          <XCircle className="mx-auto text-rose-500" size={56} />
          <h1 className="mt-6 text-3xl font-bold" data-testid="payment-failed-title">Payment not completed</h1>
          <p className="mt-2 text-muted-foreground">{res.failure_reason || "The payment was not successful."}</p>
          {res.template_id && <Button asChild className="mt-8" data-testid="retry-payment-btn"><Link to={`/checkout/${res.template_id}`}>Try again</Link></Button>}
        </>
      )}
    </div>
  );
}

export function TestPayment() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const reference = sp.get("reference");
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get(`/payments/verify/${reference}`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))); }, [reference]);
  const complete = async (outcome) => {
    try { await api.post(`/payments/test/${reference}/complete`, { outcome }); nav(`/payment/callback?reference=${reference}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="card-surface p-7 text-center" data-testid="test-payment-page">
        <span className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-500"><FlaskConical size={13} />TEST MODE — NO REAL MONEY</span>
        <h1 className="mt-5 text-2xl font-bold">Sandbox payment</h1>
        {info && <p className="mt-2 text-sm text-muted-foreground">{info.title} · <b className="text-foreground">{naira(info.amount)}</b></p>}
        <p className="mt-4 text-xs text-muted-foreground">This screen simulates a payment gateway for development. Disable it in production with ENABLE_TEST_PAYMENTS=false.</p>
        <div className="mt-7 grid gap-3">
          <Button onClick={() => complete("success")} data-testid="test-pay-success-btn">Simulate successful payment</Button>
          <Button variant="outline" onClick={() => complete("failed")} data-testid="test-pay-fail-btn">Simulate failed payment</Button>
        </div>
      </div>
    </div>
  );
}
